import { useCallback, useEffect, useMemo, useState } from 'react';
import { api } from '@/api/client';
import { extractErrorMessage } from '@/api/errors';
import { useAuth } from '@/auth/useAuth';

interface Refund {
  id: number;
  order_reference: string | null;
  appointment_id: number | null;
  customer_user_id: number;
  seller_user_id: number;
  reason: string;
  reason_note: string | null;
  gross_amount: string;
  refund_amount: string;
  refund_percent: number;
  status:
    | 'requested'
    | 'approved'
    | 'rejected'
    | 'refunded'
    | 'failed'
    | string;
  refund_method: 'gateway_reverse' | 'manual' | null;
  provider: string | null;
  provider_reference: string | null;
  decided_at: string | null;
  refunded_at: string | null;
  created_at: string;
  failure_reason: string | null;
}

const STATUS_TABS: { label: string; value: string }[] = [
  { label: 'Requested', value: 'requested' },
  { label: 'Approved', value: 'approved' },
  { label: 'Refunded', value: 'refunded' },
  { label: 'Rejected', value: 'rejected' },
  { label: 'Failed', value: 'failed' },
  { label: 'All', value: '' },
];

function formatNaira(value: string | number | null | undefined) {
  if (value == null) return '—';
  const n = typeof value === 'number' ? value : Number(value);
  if (!Number.isFinite(n)) return String(value);
  return new Intl.NumberFormat('en-NG', {
    style: 'currency',
    currency: 'NGN',
    maximumFractionDigits: 0,
  }).format(n);
}

function formatDate(iso: string | null | undefined) {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleString();
  } catch {
    return iso;
  }
}

function StatusBadge({ status }: { status: string }) {
  const tone =
    status === 'refunded'
      ? 'bg-emerald-100 text-emerald-700'
      : status === 'approved'
        ? 'bg-blue-100 text-blue-700'
        : status === 'requested'
          ? 'bg-amber-100 text-amber-700'
          : status === 'failed'
            ? 'bg-red-100 text-red-700'
            : 'bg-slate-100 text-slate-600';
  return (
    <span
      className={`rounded px-1.5 py-0.5 text-[10px] font-semibold uppercase ${tone}`}
    >
      {status}
    </span>
  );
}

export function RefundsPage() {
  const { hasPermission } = useAuth();
  const canManage = hasPermission('refunds.manage');

  const [tab, setTab] = useState<string>('requested');
  const [rows, setRows] = useState<Refund[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [acting, setActing] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const query = tab ? `?status=${encodeURIComponent(tab)}` : '';
      const res = await api.get<Refund[]>(`/back-office/refunds${query}`);
      setRows(Array.isArray(res.data) ? res.data : []);
    } catch (err) {
      setError(extractErrorMessage(err, 'Failed to load refunds'));
    } finally {
      setLoading(false);
    }
  }, [tab]);

  useEffect(() => {
    load();
  }, [load]);

  const approve = async (id: number) => {
    const overrideRaw = window.prompt(
      'Override refund percentage (1-100), leave blank for policy default:'
    );
    const override_percent = overrideRaw ? Number(overrideRaw) : undefined;
    setActing(`approve:${id}`);
    try {
      await api.post(`/back-office/refunds/${id}/approve`, {
        override_percent: Number.isFinite(override_percent)
          ? override_percent
          : undefined,
      });
      await load();
    } catch (err) {
      window.alert(extractErrorMessage(err, 'Approve failed'));
    } finally {
      setActing(null);
    }
  };

  const reject = async (id: number) => {
    const reason = window.prompt('Rejection reason (required):');
    if (!reason?.trim()) return;
    setActing(`reject:${id}`);
    try {
      await api.post(`/back-office/refunds/${id}/reject`, { reason });
      await load();
    } catch (err) {
      window.alert(extractErrorMessage(err, 'Reject failed'));
    } finally {
      setActing(null);
    }
  };

  const retry = async (id: number) => {
    setActing(`retry:${id}`);
    try {
      await api.post(`/back-office/refunds/${id}/retry`);
      await load();
    } catch (err) {
      window.alert(extractErrorMessage(err, 'Retry failed'));
    } finally {
      setActing(null);
    }
  };

  const counts = useMemo(() => rows.length, [rows]);

  return (
    <div className="px-8 py-6">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <h1 className="text-2xl font-semibold text-slate-800">Refunds</h1>
          <p className="text-sm text-slate-500">
            Customer-initiated refund requests across orders and consultations.
            Pre-settlement refunds reverse the original gateway charge; post-
            settlement refunds queue a clawback against the seller&apos;s next
            settlement.
          </p>
        </div>
      </div>

      <div className="mt-4 flex flex-wrap gap-2 border-b border-slate-200">
        {STATUS_TABS.map((t) => (
          <button
            key={t.value || 'all'}
            type="button"
            onClick={() => setTab(t.value)}
            className={`-mb-px border-b-2 px-3 py-2 text-sm font-medium ${
              tab === t.value
                ? 'border-brand-600 text-brand-700'
                : 'border-transparent text-slate-500 hover:text-slate-700'
            }`}
          >
            {t.label}
          </button>
        ))}
      </div>

      {loading && <div className="mt-6 text-sm text-slate-500">Loading…</div>}
      {!loading && error && (
        <div className="mt-6 rounded bg-red-50 px-4 py-3 text-sm text-red-700">
          {error}
        </div>
      )}

      {!loading && !error && counts === 0 && (
        <div className="mt-6 rounded border border-slate-200 bg-white px-4 py-12 text-center text-sm text-slate-500">
          No refunds match this filter.
        </div>
      )}

      {!loading && !error && counts > 0 && (
        <div className="mt-6 overflow-x-auto rounded-md border border-slate-200">
          <table className="w-full text-left text-sm">
            <thead className="bg-slate-50 text-slate-700">
              <tr>
                <th className="px-3 py-2">#</th>
                <th className="px-3 py-2">Subject</th>
                <th className="px-3 py-2">Reason</th>
                <th className="px-3 py-2">Gross</th>
                <th className="px-3 py-2">Refund</th>
                <th className="px-3 py-2">Method</th>
                <th className="px-3 py-2">Status</th>
                <th className="px-3 py-2">Created</th>
                <th className="px-3 py-2"></th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => (
                <tr key={r.id} className="border-t border-slate-100 align-top">
                  <td className="px-3 py-2 font-mono text-xs">#{r.id}</td>
                  <td className="px-3 py-2 text-xs">
                    {r.order_reference ? (
                      <a
                        href={`/orders/${r.order_reference}`}
                        className="text-brand-600 hover:underline"
                      >
                        Order {r.order_reference}
                      </a>
                    ) : r.appointment_id ? (
                      <a
                        href={`/consultations/${r.appointment_id}`}
                        className="text-brand-600 hover:underline"
                      >
                        Consult #{r.appointment_id}
                      </a>
                    ) : (
                      '—'
                    )}
                  </td>
                  <td
                    className="px-3 py-2 text-xs text-slate-600 max-w-[200px] truncate"
                    title={r.reason_note ?? ''}
                  >
                    {r.reason}
                    {r.reason_note ? ` · ${r.reason_note}` : ''}
                  </td>
                  <td className="px-3 py-2">{formatNaira(r.gross_amount)}</td>
                  <td className="px-3 py-2">
                    {formatNaira(r.refund_amount)}
                    <span className="ml-1 text-[10px] text-slate-400">
                      ({r.refund_percent}%)
                    </span>
                  </td>
                  <td className="px-3 py-2 text-xs uppercase text-slate-500">
                    {r.refund_method ?? '—'}
                  </td>
                  <td className="px-3 py-2">
                    <StatusBadge status={r.status} />
                    {r.failure_reason && (
                      <div
                        className="mt-1 max-w-[180px] truncate text-[10px] text-red-600"
                        title={r.failure_reason}
                      >
                        {r.failure_reason}
                      </div>
                    )}
                  </td>
                  <td className="px-3 py-2 text-xs text-slate-500">
                    {formatDate(r.created_at)}
                  </td>
                  <td className="px-3 py-2 text-right">
                    <div className="flex flex-col items-end gap-1">
                      {r.status === 'requested' && (
                        <>
                          <button
                            type="button"
                            disabled={
                              !canManage || acting === `approve:${r.id}`
                            }
                            onClick={() => approve(r.id)}
                            className="text-xs font-medium text-brand-600 hover:underline disabled:opacity-50"
                          >
                            {acting === `approve:${r.id}`
                              ? 'Approving…'
                              : 'Approve'}
                          </button>
                          <button
                            type="button"
                            disabled={!canManage || acting === `reject:${r.id}`}
                            onClick={() => reject(r.id)}
                            className="text-xs text-slate-600 hover:text-slate-900 hover:underline disabled:opacity-50"
                          >
                            Reject
                          </button>
                        </>
                      )}
                      {r.status === 'failed' && (
                        <button
                          type="button"
                          disabled={!canManage || acting === `retry:${r.id}`}
                          onClick={() => retry(r.id)}
                          className="text-xs font-medium text-brand-600 hover:underline disabled:opacity-50"
                        >
                          {acting === `retry:${r.id}` ? 'Retrying…' : 'Retry'}
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

export default RefundsPage;
